import React, { useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import { formatFileSize, getFileTypeCategory, getFileIconByExtension } from '@/constants/utils/files';
import { getLibraryPalette } from './libraryPalette';

export const FILE_CATEGORIES = [
  { id: 'all', label: 'All files', icon: 'files' },
  { id: 'documents', label: 'Documents', icon: 'file-lines' },
  { id: 'images', label: 'Images', icon: 'image' },
  { id: 'audio', label: 'Audio', icon: 'music' },
  { id: 'video', label: 'Video', icon: 'film' },
  { id: 'code', label: 'Code', icon: 'code' },
  { id: 'other', label: 'Other', icon: 'file' },
];

function FileMenu({ file, onOpen, onDetails, onDownload, onChat, onCollect, palette }) {
  const actions = [
    { id: 'open', title: 'Open file', icon: 'arrow-up-right-from-square', iosIcon: 'arrow.up.right.square', onSelect: () => onOpen(file) },
    { id: 'details', title: 'Details & rename', icon: 'pencil', iosIcon: 'pencil', onSelect: () => onDetails(file) },
    { id: 'download', title: 'Download', icon: 'arrow-down-to-line', iosIcon: 'arrow.down.to.line', onSelect: () => onDownload(file) },
    { id: 'chat', title: 'Chat with file', icon: 'message-dots', iosIcon: 'bubble.left', onSelect: () => onChat(file) },
    { id: 'collection', title: 'Add to collection', icon: 'folder-plus', iosIcon: 'folder.badge.plus', onSelect: () => onCollect(file) },
  ];
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild={Platform.OS === 'web' || Platform.OS === 'macos'}>
        <Pressable accessibilityRole="button" accessibilityLabel={`Actions for ${file.name}`}
          style={({ hovered, pressed }) => ({ width: 32, height: 32, borderRadius: 9, alignItems: 'center', justifyContent: 'center', backgroundColor: hovered || pressed ? palette.hover : palette.surface })}>
          <Icon name="ellipsis" size={16} color={palette.icon} />
        </Pressable>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" sideOffset={6} className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}>
        {actions.map(action => Platform.OS === 'web'
          ? <DropdownMenuItem key={action.id} {...action} />
          : <DropdownMenu.Item key={action.id} onSelect={action.onSelect}>
            <DropdownMenu.ItemIcon ios={{ name: action.iosIcon }} />
            <DropdownMenu.ItemTitle>{action.title}</DropdownMenu.ItemTitle>
          </DropdownMenu.Item>)}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

export default React.memo(function LibraryFileItem({ file, width, list, compact, selected, selectionDisabled, onSelect, ...actions }) {
  const { colors, theme } = useTheme();
  const palette = getLibraryPalette(theme);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const category = getFileTypeCategory(file);
  const extension = String(file.extension || file.name?.split('.').pop() || 'file').replace(/^\./, '');
  const thumb = category === 'images' && !imageFailed ? (file.thumbnailPath || file.cdnPath) : null;
  const status = file.processingStatus?.status;
  const date = new Date(file.createdAt);
  const dateLabel = Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const checkbox = (
    <Pressable accessibilityRole="checkbox" accessibilityLabel={`Select ${file.name}`}
      accessibilityState={{ checked: selected, disabled: selectionDisabled }} disabled={selectionDisabled}
      aria-checked={selected}
      onPress={() => onSelect(file)}
      style={({ hovered, pressed }) => ({ width: 32, height: 32, alignItems: 'center', justifyContent: 'center', borderRadius: 9, backgroundColor: hovered || pressed ? palette.hover : palette.surface })}>
      <View style={{ width: 17, height: 17, borderRadius: 5, borderWidth: 1, borderColor: selected ? palette.ink : palette.icon, backgroundColor: selected ? palette.ink : 'transparent', alignItems: 'center', justifyContent: 'center', opacity: selectionDisabled ? 0.35 : 1 }}>
        {selected && <Icon name="check" size={10} color={palette.onInk} />}
      </View>
    </Pressable>
  );
  const preview = (
    <View style={{ height: list ? 40 : compact ? 120 : 140, width: list ? 40 : '100%', flexShrink: 0, borderRadius: list ? 10 : 0, backgroundColor: palette.preview, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      {thumb ? <Image source={{ uri: thumb }} contentFit={list ? 'cover' : 'contain'} style={{ width: '100%', height: '100%' }} onError={() => setImageFailed(true)} accessibilityLabel={file.name} />
        : <View style={{ padding: list ? 0 : 18 }}>
          <Icon name={getFileIconByExtension(extension)} size={list ? 19 : 38} color={palette.icon} />
        </View>}
    </View>
  );
  const metadata = (
    <Text size="xs" color="secondary" numberOfLines={1} style={{ lineHeight: 17 }}>
      {String(extension).toUpperCase()} · {formatFileSize(file.sizeInBytes)}
    </Text>
  );
  const processing = status && !['completed', 'skipped'].includes(status)
    ? <Text size="xxs" style={{ color: status === 'failed' ? colors.color_destructive : colors.text_color_secondary }}>{status === 'failed' ? 'Processing failed' : 'Preparing for chat…'}</Text> : null;

  if (list) return (
    <View style={{ flexDirection: 'row', alignItems: 'center', padding: compact ? 10 : 14, gap: compact ? 8 : 12, borderBottomWidth: 1, borderColor: palette.border, backgroundColor: selected ? palette.selected : hovered || focused ? palette.hover : palette.surface }}>
      {checkbox}
      <Pressable accessibilityRole="button" accessibilityLabel={`Open ${file.name}`} onPress={() => actions.onOpen(file)} onHoverIn={() => setHovered(true)} onHoverOut={() => setHovered(false)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} style={{ flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: 12 }}>
        {preview}
        <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
          <Text size="s" weight="medium" numberOfLines={1}>{file.name}</Text>
          {metadata}{processing}
        </View>
        {!compact && <Text size="xs" color="secondary" style={{ width: 100, textAlign: 'right' }}>{dateLabel}</Text>}
      </Pressable>
      <FileMenu file={file} palette={palette} {...actions} />
    </View>
  );

  return (
    <View style={{ width, borderRadius: 18, borderWidth: 1, borderColor: selected ? palette.ink : hovered || focused ? palette.activeBorder : palette.border, backgroundColor: selected ? palette.selected : palette.surface, overflow: 'hidden', ...(Platform.OS === 'web' && { transition: 'border-color 160ms ease, background-color 160ms ease' }) }}>
      <Pressable accessibilityRole="button" accessibilityLabel={`Open ${file.name}`} onPress={() => actions.onOpen(file)} onHoverIn={() => setHovered(true)} onHoverOut={() => setHovered(false)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} style={({ pressed }) => ({ opacity: pressed ? 0.75 : 1 })}>
        {preview}
        <View style={{ padding: compact ? 12 : 16, gap: 6 }}>
          <Text size="s" weight="semibold" numberOfLines={2} style={{ height: 40, lineHeight: 20, ...(Platform.OS === 'web' && { overflowWrap: 'anywhere' }) }}>{file.name}</Text>
          {metadata}
          {processing}
        </View>
        <View style={{ minHeight: 36, paddingHorizontal: compact ? 12 : 16, paddingVertical: 10, borderTopWidth: 1, borderTopColor: palette.border }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
            <Text size="xxs" color="tertiary">{dateLabel}</Text>
            <Text size="xxs" color="tertiary">{file.source === 'inference' ? 'Generated' : 'Uploaded'}</Text>
          </View>
        </View>
      </Pressable>
      <View style={{ position: 'absolute', top: 8, left: 8 }}>{checkbox}</View>
      <View style={{ position: 'absolute', top: 8, right: 8 }}><FileMenu file={file} palette={palette} {...actions} /></View>
    </View>
  );
});
